import { createClient } from '@supabase/supabase-js';
import { DEMO_ACCOUNTS_BY_KEY, type DemoAccountKey } from '@perdiem/trpc/demo-accounts';
import * as SecureStore from 'expo-secure-store';
import { AppState, Platform } from 'react-native';
import type { Session, AuthChangeEvent, Subscription } from '@supabase/supabase-js';
import { resolveDevelopmentUrl } from './dev-url';
import { markAuthEntrySeen } from './auth-device-state';

// ── Dev bypass ────────────────────────────────────────────────────────────────
// When EXPO_PUBLIC_DEV_BYPASS=true the app bypasses real Supabase auth.
// Any Bearer token is accepted by the API (DEV_BYPASS_AUTH=true), and the
// fake session here supplies the token + user ID used to look up the seeded
// dev person (supabaseUserId = '00000000-0000-0000-0000-000000000001').

const DEV_BYPASS = process.env['EXPO_PUBLIC_DEV_BYPASS'] === 'true';
const DEMO_SESSION_STORAGE_KEY = 'perdiem.demo-account';
const LOCAL_DEV_SESSION_STORAGE_KEY = 'perdiem.local-dev-user';

const DEV_SESSION: Session = {
  access_token: 'dev-bypass',
  refresh_token: 'dev-bypass-refresh',
  expires_in: 9_999_999,
  token_type: 'bearer',
  user: {
    id: '00000000-0000-0000-0000-000000000001',
    email: 'dev@perdiem.app',
    app_metadata: {},
    user_metadata: {},
    aud: 'authenticated',
    created_at: new Date().toISOString(),
  },
};

/** Minimal mock of the Supabase auth client surface used by this app. */
const devSupabase = {
  auth: {
    getSession: async () => ({ data: { session: DEV_SESSION }, error: null }),
    onAuthStateChange: (cb: (event: AuthChangeEvent, session: Session | null) => void) => {
      // Fire immediately so _layout.tsx sees the session on first render.
      setTimeout(() => cb('SIGNED_IN', DEV_SESSION), 0);
      const subscription: Subscription = { id: 'dev', callback: cb, unsubscribe: () => {} };
      return { data: { subscription } };
    },
    signInWithOAuth: async () => ({ data: { provider: 'google', url: '' }, error: null }),
    signOut: async () => ({ error: null }),
  },
};

if (DEV_BYPASS) {
  console.log('[dev] Auth bypass enabled — using fake Supabase session');
}

type AuthStateChangeCallback = (event: AuthChangeEvent, session: Session | null) => void;
type LocalDevUser = {
  supabaseUserId: string;
  email: string | null;
  displayName?: string | null;
};

function getWebStorage() {
  if (Platform.OS !== 'web' || typeof localStorage === 'undefined') {
    return null;
  }

  if (
    typeof localStorage.getItem !== 'function' ||
    typeof localStorage.setItem !== 'function' ||
    typeof localStorage.removeItem !== 'function'
  ) {
    return null;
  }

  return localStorage;
}

function createDemoSession(accountKey: DemoAccountKey): Session {
  const account = DEMO_ACCOUNTS_BY_KEY[accountKey];

  return {
    access_token: account.token,
    refresh_token: `${account.token}-refresh`,
    expires_in: 9_999_999,
    token_type: 'bearer',
    user: {
      id: account.supabaseUserId,
      email: account.email,
      app_metadata: {
        provider: 'demo',
        isDemo: true,
        demoAccountKey: account.key,
      },
      user_metadata: {
        demoAccountKey: account.key,
      },
      aud: 'authenticated',
      created_at: new Date('2026-01-01T00:00:00.000Z').toISOString(),
    },
  };
}

function buildLocalDevAccessToken(user: LocalDevUser): string {
  return `local-dev:${user.supabaseUserId}:${encodeURIComponent(user.email ?? '')}`;
}

function createLocalDevSession(user: LocalDevUser): Session {
  return {
    access_token: buildLocalDevAccessToken(user),
    refresh_token: `${user.supabaseUserId}-local-dev-refresh`,
    expires_in: 9_999_999,
    token_type: 'bearer',
    user: {
      id: user.supabaseUserId,
      email: user.email ?? undefined,
      app_metadata: {
        provider: 'local-dev',
        isLocalDev: true,
      },
      user_metadata: {
        displayName: user.displayName ?? null,
      },
      aud: 'authenticated',
      created_at: new Date('2026-01-01T00:00:00.000Z').toISOString(),
    },
  };
}

function readStoredDemoAccountKey(): DemoAccountKey | null {
  const storage = getWebStorage();
  const rawValue = storage?.getItem(DEMO_SESSION_STORAGE_KEY);

  if (!rawValue || !(rawValue in DEMO_ACCOUNTS_BY_KEY)) {
    return null;
  }

  return rawValue as DemoAccountKey;
}

function persistDemoAccountKey(accountKey: DemoAccountKey | null) {
  const storage = getWebStorage();
  if (!storage) return;

  if (accountKey) {
    storage.setItem(DEMO_SESSION_STORAGE_KEY, accountKey);
  } else {
    storage.removeItem(DEMO_SESSION_STORAGE_KEY);
  }
}

function readStoredLocalDevUser(): LocalDevUser | null {
  const storage = getWebStorage();
  const rawValue = storage?.getItem(LOCAL_DEV_SESSION_STORAGE_KEY);
  if (!rawValue) return null;

  try {
    const parsed = JSON.parse(rawValue) as Partial<LocalDevUser>;
    if (typeof parsed.supabaseUserId !== 'string') {
      return null;
    }

    return {
      supabaseUserId: parsed.supabaseUserId,
      email: typeof parsed.email === 'string' ? parsed.email : null,
      displayName: typeof parsed.displayName === 'string' ? parsed.displayName : null,
    };
  } catch {
    return null;
  }
}

function persistLocalDevUser(user: LocalDevUser | null) {
  const storage = getWebStorage();
  if (!storage) return;

  if (user) {
    storage.setItem(LOCAL_DEV_SESSION_STORAGE_KEY, JSON.stringify(user));
  } else {
    storage.removeItem(LOCAL_DEV_SESSION_STORAGE_KEY);
  }
}

const syntheticAuthListeners = new Set<AuthStateChangeCallback>();
let demoSession = (() => {
  const storedKey = readStoredDemoAccountKey();
  return storedKey ? createDemoSession(storedKey) : null;
})();
let localDevSession = (() => {
  const storedUser = readStoredLocalDevUser();
  return storedUser ? createLocalDevSession(storedUser) : null;
})();

function getSyntheticSession(): Session | null {
  return localDevSession ?? demoSession;
}

function clearSyntheticSessions() {
  demoSession = null;
  localDevSession = null;
  persistDemoAccountKey(null);
  persistLocalDevUser(null);
}

export function clearSyntheticSessionForRedirect(): boolean {
  const hadSyntheticSession = getSyntheticSession() !== null;
  clearSyntheticSessions();
  return hadSyntheticSession;
}

function notifySyntheticAuthListeners(event: AuthChangeEvent, session: Session | null) {
  for (const listener of syntheticAuthListeners) {
    listener(event, session);
  }
}

export function isDemoSession(session: Session | null | undefined): boolean {
  return session?.user.app_metadata?.['isDemo'] === true;
}

export function isLocalDevSession(session: Session | null | undefined): boolean {
  return session?.user.app_metadata?.['isLocalDev'] === true;
}

export async function signInDemoAccount(accountKey: DemoAccountKey): Promise<Session> {
  const nextSession = createDemoSession(accountKey);

  localDevSession = null;
  persistLocalDevUser(null);
  demoSession = nextSession;
  persistDemoAccountKey(accountKey);

  try {
    await realSupabase.auth.signOut();
  } catch {
    // Ignore stale real-session cleanup failures when entering demo mode.
  }

  notifySyntheticAuthListeners('SIGNED_IN', nextSession);
  return nextSession;
}

export async function signInLocalDevUser(user: LocalDevUser): Promise<Session> {
  const nextSession = createLocalDevSession(user);

  demoSession = null;
  persistDemoAccountKey(null);
  localDevSession = nextSession;
  persistLocalDevUser(user);

  try {
    await realSupabase.auth.signOut();
  } catch {
    // Ignore stale real-session cleanup failures when entering local dev mode.
  }

  notifySyntheticAuthListeners('SIGNED_IN', nextSession);
  return nextSession;
}

// ── Production Supabase client ─────────────────────────────────────────────

const getSupabaseUrl = () => {
  const url = process.env['EXPO_PUBLIC_SUPABASE_URL']!;
  return resolveDevelopmentUrl(url);
};

const supabaseUrl = getSupabaseUrl();
const supabaseAnonKey = process.env['EXPO_PUBLIC_SUPABASE_ANON_KEY']!;
const supabaseStorageKey = `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;
const INVALID_REFRESH_TOKEN_CODES = new Set(['refresh_token_not_found', 'refresh_token_already_used']);

/**
 * Expo Secure Store adapter for Supabase session persistence.
 * Falls back to in-memory storage on web.
 */
const ExpoSecureStoreAdapter = {
  getItem: (key: string): string | null | Promise<string | null> => {
    if (Platform.OS === 'web') {
      const webStorage =
        typeof localStorage !== 'undefined' &&
        typeof localStorage.getItem === 'function'
          ? localStorage
          : null;
      return webStorage ? webStorage.getItem(key) : null;
    }
    return SecureStore.getItemAsync(key);
  },
  setItem: (key: string, value: string): void | Promise<void> => {
    if (Platform.OS === 'web') {
      const webStorage =
        typeof localStorage !== 'undefined' &&
        typeof localStorage.setItem === 'function'
          ? localStorage
          : null;
      if (webStorage) webStorage.setItem(key, value);
      return;
    }
    return SecureStore.setItemAsync(key, value);
  },
  removeItem: (key: string): void | Promise<void> => {
    if (Platform.OS === 'web') {
      const webStorage =
        typeof localStorage !== 'undefined' &&
        typeof localStorage.removeItem === 'function'
          ? localStorage
          : null;
      if (webStorage) webStorage.removeItem(key);
      return;
    }
    return SecureStore.deleteItemAsync(key);
  },
};

async function clearPersistedSupabaseSession() {
  await Promise.all([
    ExpoSecureStoreAdapter.removeItem(supabaseStorageKey),
    ExpoSecureStoreAdapter.removeItem(`${supabaseStorageKey}-code-verifier`),
    ExpoSecureStoreAdapter.removeItem(`${supabaseStorageKey}-user`),
  ]);
}

export async function recoverFromAuthError() {
  if (getSyntheticSession()) {
    clearSyntheticSessions();
    notifySyntheticAuthListeners('SIGNED_OUT', null);
    return;
  }

  let signOutError: unknown = null;

  try {
    const { error } = await realSupabase.auth.signOut({ scope: 'local' });
    signOutError = error;
  } catch (error) {
    signOutError = error;
  } finally {
    await clearPersistedSupabaseSession();
  }

  if (signOutError && !isInvalidRefreshTokenError(signOutError)) {
    console.warn('[auth] Failed to sign out cleanly after auth error', signOutError);
  }
}

function isInvalidRefreshTokenError(error: unknown): error is { code?: string; message?: string } {
  if (!error || typeof error !== 'object') return false;

  const code = 'code' in error && typeof error.code === 'string' ? error.code : null;
  const message = 'message' in error && typeof error.message === 'string' ? error.message : null;

  return (
    (code !== null && INVALID_REFRESH_TOKEN_CODES.has(code)) ||
    message === 'Invalid Refresh Token: Refresh Token Not Found'
  );
}

const realSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: ExpoSecureStoreAdapter,
    storageKey: supabaseStorageKey,
    autoRefreshToken: true,
    persistSession: true,
    // Enable on web so Supabase automatically exchanges the OAuth code from the
    // callback URL for a session (PKCE flow).
    detectSessionInUrl: Platform.OS === 'web',
  },
});

const syntheticCapableAuth = new Proxy(realSupabase.auth as object, {
  get(target, prop, receiver) {
    if (prop === 'getSession') {
      return async () => {
        const syntheticSession = getSyntheticSession();
        if (syntheticSession) {
          return { data: { session: syntheticSession }, error: null };
        }

        return Reflect.get(target, prop, receiver).call(target);
      };
    }

    if (prop === 'onAuthStateChange') {
      return (callback: AuthStateChangeCallback) => {
        syntheticAuthListeners.add(callback);

        const realListener = realSupabase.auth.onAuthStateChange((event, session) => {
          if (getSyntheticSession()) return;
          callback(event, session);
        });

        const syntheticSession = getSyntheticSession();
        if (syntheticSession) {
          setTimeout(() => callback('SIGNED_IN', syntheticSession), 0);
        }

        const subscription: Subscription = {
          id: `synthetic-${String(realListener.data.subscription.id)}`,
          callback,
          unsubscribe: () => {
            syntheticAuthListeners.delete(callback);
            realListener.data.subscription.unsubscribe();
          },
        };

        return { data: { subscription } };
      };
    }

    if (prop === 'signOut') {
      return async (...args: unknown[]) => {
        if (getSyntheticSession()) {
          clearSyntheticSessions();
          notifySyntheticAuthListeners('SIGNED_OUT', null);
          return { error: null };
        }

        return Reflect.get(target, prop, receiver).call(target, ...args);
      };
    }

    const value = Reflect.get(target, prop, receiver);
    return typeof value === 'function' ? value.bind(target) : value;
  },
});

type GlobalSupabaseState = typeof globalThis & {
  __perdiemSupabaseAuthInitPromise?: Promise<void>;
  __perdiemSupabaseAppStateListener?: { remove: () => void };
};

const globalSupabaseState = globalThis as GlobalSupabaseState;

export async function initializeSupabaseAuth() {
  if (DEV_BYPASS) return;

  if (!globalSupabaseState.__perdiemSupabaseAuthInitPromise) {
    globalSupabaseState.__perdiemSupabaseAuthInitPromise = (async () => {
      const { data, error } = await realSupabase.auth.getSession();

      if (isInvalidRefreshTokenError(error)) {
        console.warn('[auth] Clearing invalid persisted Supabase session');
        await markAuthEntrySeen();
        await clearPersistedSupabaseSession();
      }

      if (
        Platform.OS !== 'web' &&
        !globalSupabaseState.__perdiemSupabaseAppStateListener
      ) {
        globalSupabaseState.__perdiemSupabaseAppStateListener = AppState.addEventListener(
          'change',
          (state) => {
            if (state === 'active') {
              realSupabase.auth.startAutoRefresh();
            } else {
              realSupabase.auth.stopAutoRefresh();
            }
          },
        );
      }

      if (
        Platform.OS !== 'web' &&
        data.session &&
        AppState.currentState === 'active'
      ) {
        realSupabase.auth.startAutoRefresh();
      }
    })();
  }

  try {
    await globalSupabaseState.__perdiemSupabaseAuthInitPromise;
  } catch (error) {
    globalSupabaseState.__perdiemSupabaseAuthInitPromise = undefined;
    throw error;
  }
}

// Export the appropriate client based on the bypass flag.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const supabase: typeof realSupabase = DEV_BYPASS
  ? (devSupabase as any)
  : (new Proxy(realSupabase as object, {
      get(target, prop, receiver) {
        if (prop === 'auth') {
          return syntheticCapableAuth;
        }

        const value = Reflect.get(target, prop, receiver);
        return typeof value === 'function' ? value.bind(target) : value;
      },
    }) as any);
